import React, { useState, useEffect, useRef } from 'react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import { useMobileOptimization, useTouchGestures } from '../hooks/useMobileFirst.jsx'

// Componente de mapa otimizado para mobile
export const MobileMap = ({ 
  center, 
  zoom = 15, 
  markers = [], 
  onMarkerClick,
  className = '',
  height = '300px',
  ...props 
}) => {
  const { isMobile, isTouchDevice, viewportHeight } = useMobileOptimization()
  const { gestures, touchHandlers } = useTouchGestures()
  const [mapHeight, setMapHeight] = useState(height)
  const mapRef = useRef(null)

  // Ajustar altura do mapa baseado no viewport
  useEffect(() => {
    if (isMobile) {
      const mobileHeight = Math.min(viewportHeight * 0.4, 300)
      setMapHeight(`${mobileHeight}px`)
    } else {
      setMapHeight(height)
    }
  }, [isMobile, viewportHeight, height])

  // Otimizar zoom para mobile
  const optimizedZoom = isMobile ? Math.max(zoom - 2, 10) : zoom

  // Detectar gestos de swipe para navegação
  useEffect(() => {
    if (gestures.swipeLeft) {
      // Swipe left - próximo marcador
      console.log('Swipe left detected')
    }
    if (gestures.swipeRight) {
      // Swipe right - marcador anterior
      console.log('Swipe right detected')
    }
  }, [gestures])

  return (
    <div 
      className={`relative ${className}`}
      style={{ height: mapHeight }}
      {...touchHandlers}
    >
      <MapContainer
        center={center}
        zoom={optimizedZoom}
        style={{ height: '100%', width: '100%' }}
        className="rounded-lg"
        ref={mapRef}
        {...props}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        {markers.map((marker, index) => (
          <Marker
            key={marker.id || index}
            position={[marker.lat, marker.lng]}
            eventHandlers={{
              click: () => onMarkerClick && onMarkerClick(marker, index)
            }}
          >
            <Popup>
              <div className="text-center">
                <strong>{marker.name}</strong>
                {marker.description && (
                  <>
                    <br />
                    <small>{marker.description}</small>
                  </>
                )}
                {marker.updatedAt && (
                  <>
                    <br />
                    <small className="text-gray-500">
                      Atualizado: {new Date(marker.updatedAt).toLocaleString()}
                    </small>
                  </>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Controles mobile */}
      {isMobile && (
        <div className="absolute top-2 right-2 flex flex-col gap-2">
          <button
            className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center"
            onClick={() => {
              if (mapRef.current) {
                mapRef.current.setZoom(mapRef.current.getZoom() + 1)
              }
            }}
          >
            <span className="text-lg font-bold">+</span>
          </button>
          <button
            className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center"
            onClick={() => {
              if (mapRef.current) {
                mapRef.current.setZoom(mapRef.current.getZoom() - 1)
              }
            }}
          >
            <span className="text-lg font-bold">-</span>
          </button>
        </div>
      )}
    </div>
  )
}

// Componente de lista de localizações mobile
export const MobileLocationList = ({ 
  locations, 
  onLocationSelect,
  onLocationUpdate,
  className = ''
}) => {
  const { isMobile, isTouchDevice } = useMobileOptimization()
  const { gestures, touchHandlers } = useTouchGestures()

  const formatDistance = (distance) => {
    if (distance < 1000) {
      return `${Math.round(distance)}m`
    }
    return `${(distance / 1000).toFixed(1)}km`
  }

  const formatTimeAgo = (timestamp) => {
    const now = new Date()
    const time = new Date(timestamp)
    const diff = now - time
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Agora'
    if (minutes < 60) return `${minutes}min atrás`
    if (hours < 24) return `${hours}h atrás`
    return `${days}d atrás`
  }

  return (
    <div className={`space-y-3 ${className}`} {...touchHandlers}>
      {locations.map((location, index) => (
        <div
          key={location.id || index}
          className={`
            bg-white rounded-lg p-4 shadow-sm border
            ${isTouchDevice ? 'min-h-[60px]' : ''}
            ${onLocationSelect ? 'cursor-pointer hover:shadow-md active:scale-95' : ''}
            transition-all duration-200
          `}
          onClick={() => onLocationSelect && onLocationSelect(location, index)}
        >
          <div className="flex items-start gap-3">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {location.photoUrl ? (
                <img
                  src={location.photoUrl}
                  alt={location.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center">
                  <span className="text-gray-600 font-semibold">
                    {location.name?.charAt(0) || '?'}
                  </span>
                </div>
              )}
            </div>

            {/* Informações */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-gray-900 truncate">
                  {location.name}
                </h3>
                <span className="text-xs text-gray-500">
                  {formatTimeAgo(location.updatedAt)}
                </span>
              </div>

              {/* Status */}
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Online</span>
                {location.distance && (
                  <>
                    <span className="text-gray-400">•</span>
                    <span className="text-sm text-gray-600">
                      {formatDistance(location.distance)}
                    </span>
                  </>
                )}
              </div>

              {/* Coordenadas */}
              {location.lat && location.lng && (
                <p className="text-xs text-gray-500">
                  📍 {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                </p>
              )}
            </div>

            {/* Botão de ação */}
            {onLocationUpdate && (
              <button
                className="flex-shrink-0 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                onClick={(e) => {
                  e.stopPropagation()
                  onLocationUpdate(location, index)
                }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

// Componente de botão SOS otimizado para mobile
export const MobileSOSButton = ({ 
  onPress,
  disabled = false,
  loading = false,
  className = ''
}) => {
  const { isMobile, isTouchDevice, touchTargetSize } = useMobileOptimization()
  const { gestures, touchHandlers } = useTouchGestures()

  return (
    <button
      className={`
        ${isMobile ? 'w-full' : 'w-auto'}
        ${isTouchDevice ? `min-h-[${touchTargetSize}px]` : 'h-14'}
        bg-red-600 text-white font-bold text-lg
        rounded-xl shadow-lg hover:bg-red-700 active:bg-red-800
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-all duration-200 active:scale-95
        flex items-center justify-center gap-3
        ${className}
      `}
      onClick={onPress}
      disabled={disabled || loading}
      {...touchHandlers}
    >
      {loading ? (
        <>
          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          <span>Enviando SOS...</span>
        </>
      ) : (
        <>
          <span className="text-2xl animate-pulse">🚨</span>
          <span>ENVIAR SOS</span>
        </>
      )}
    </button>
  )
}

export default {
  MobileMap,
  MobileLocationList,
  MobileSOSButton
}
